import React, { useCallback } from "react";
import {
  Tooltip,
  IconButton,
  Button,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import styled from "styled-components";
import DeleteIcon from "@material-ui/icons/Delete";
import CancelIcon from "@material-ui/icons/Cancel";
import SaveIcon from "@material-ui/icons/Save";
import StopIcon from "@material-ui/icons/Stop";
import ResetIcon from "@material-ui/icons/Autorenew";
import EditIcon from "@material-ui/icons/Edit";
import PhotoSizeSelectLargeIcon from "@material-ui/icons/PhotoSizeSelectLarge";
import { TCalibration } from "./Calibration";
import { TModel } from "./models";
import PreviousIcon from '@material-ui/icons/NavigateBefore';
import NextIcon from '@material-ui/icons/NavigateNext';

type Props = {
  editing: boolean;
  toggleEdit: () => void;
  projecting: boolean;
  toggleProjection: () => void;
  pitchProjection: boolean;
  togglePitchProjection: () => void;
  calibrate?: () => void;
  candidates: Array<TCalibration>;
  saveCalibration: () => void;
  advanced: boolean;
  deleteCandidate: () => void;
  stopCalibration?: () => void;
  candidateIndex: number;
  image: HTMLImageElement;
  model: TModel;
  previousCalibration?: TCalibration;
  setCandidateIndex: (index: number) => void;
  setAdvanced: (advanced: boolean) => void;
  setCandidates: (candidates: Array<TCalibration>) => void;
  setDraggable: (draggable: boolean) => void;
  readonly?: boolean;
};

export function ButtonContainer({
  editing,
  toggleEdit,
  projecting,
  toggleProjection,
  pitchProjection,
  togglePitchProjection,
  calibrate,
  candidates,
  saveCalibration,
  advanced,
  deleteCandidate,
  stopCalibration,
  candidateIndex,
  image,
  model,
  previousCalibration,
  setCandidateIndex,
  setAdvanced,
  setCandidates,
  setDraggable,
  readonly,
}: Props) {
  const resetCalibration = useCallback(() => {
    setCandidates([]);
  }, [setCandidates]);

  const previousCandidate = useCallback(() => {
    let newindex = candidateIndex - 1;
    if (newindex < 0) newindex = (candidates?.length || 0) - 1;
    setCandidateIndex(newindex);
  }, [candidateIndex, candidates, setCandidateIndex]);
  const nextCandidate = useCallback(() => {
    let newindex = candidateIndex + 1;
    if (newindex + 1 > (candidates?.length || 0)) newindex = 0;
    setCandidateIndex(newindex);
  }, [candidateIndex, candidates, setCandidateIndex]);

  React.useEffect(() => {
    const keydown = (e: any) => {
      if (e.code === "ArrowRight" && advanced) {
        e.preventDefault();
        e.stopPropagation();
        nextCandidate();
      } else if (e.code === "ArrowLeft" && advanced) {
        e.preventDefault();
        e.stopPropagation();
        previousCandidate();
      } else if (e.code === "KeyC" && calibrate) {
        e.preventDefault();
        e.stopPropagation();
        calibrate();
      } else if (e.code === "KeyS") {
        e.preventDefault();
        e.stopPropagation();
        saveCalibration();
      } else if (e.code === "KeyD") {
        e.preventDefault();
        e.stopPropagation();
        deleteCandidate();
      } else if (e.code === "KeyE") {
        e.preventDefault();
        e.stopPropagation();
        toggleEdit();
      } else if (e.code === "KeyA") {
        e.preventDefault();
        e.stopPropagation();
        setAdvanced(!advanced);
      } else if (e.code === "KeyX") {
        e.preventDefault();
        e.stopPropagation();
        resetCalibration();
      } else if (e.code === "KeyL") {
        e.preventDefault();
        e.stopPropagation();
        console.log(candidates[candidateIndex]);
      } else if (e.code === "KeyQ" && stopCalibration) {
        e.preventDefault();
        e.stopPropagation();
        stopCalibration();
      } else if (e.key === "Shift") {
        e.preventDefault();
        e.stopPropagation();
        setDraggable(true);
      }
    };

    const keyup = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        e.preventDefault();
        e.stopPropagation();
        setDraggable(false);
      }
    };

    document.addEventListener("keydown", keydown);
    document.addEventListener("keyup", keyup);
    return () => {
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("keyup", keyup);
    };
  }, [
    resetCalibration,
    candidateIndex,
    candidates,
    calibrate,
    saveCalibration,
    advanced,
    toggleEdit,
    stopCalibration,
    setCandidateIndex,
    deleteCandidate,
    setAdvanced,
    setCandidates,
    setDraggable,
    previousCandidate,
    nextCandidate,
  ]);

  const viewButtons = () => (
    <>
      <FormControlLabel
        control={
          <Switch
            style={{ marginLeft: "10px" }}
            checked={projecting}
            onChange={toggleProjection}
            name="projection"
            color="primary"
          />
        }
        label="Line projection"
      />
      <FormControlLabel
        control={
          <Switch
            style={{ marginLeft: "10px" }}
            checked={pitchProjection}
            onChange={togglePitchProjection}
            name="pitchprojection"
            color="primary"
          />
        }
        label="Pitch projection"
      />
      {!readonly && (
        <Tooltip title={"Edit calibration"} aria-label="edit">
          <span>
            <IconButton color="primary" aria-label="edit" onClick={toggleEdit}>
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );

  const editingButtons = () => (
    <>
      <Tooltip title={"Cancel calibration"} aria-label="edit">
        <span>
          <IconButton color="primary" aria-label="edit" onClick={toggleEdit}>
            <CancelIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Calibrate" aria-label="calibrate">
        <span>
          <IconButton
            disabled={!image || !model || !calibrate}
            color="primary"
            aria-label="calibrate"
            onClick={calibrate}
          >
            <PhotoSizeSelectLargeIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Save" aria-label="save">
        <span>
          <IconButton
            disabled={
              !(
                candidates &&
                candidates.length > 0 &&
                candidates[candidateIndex] !== previousCalibration
              )
            }
            color="primary"
            aria-label="save"
            onClick={saveCalibration}
          >
            <SaveIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Stop calibration" aria-label="stop">
        <span>
          <IconButton
            disabled={!stopCalibration}
            color="primary"
            aria-label="stop"
            onClick={stopCalibration}
          >
            <StopIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Reset calibration" aria-label="reset">
        <span>
          <IconButton
            disabled={stopCalibration !== undefined || candidates.length === 0}
            color="primary"
            aria-label="stop"
            onClick={resetCalibration}
          >
            <ResetIcon />
          </IconButton>
        </span>
      </Tooltip>
      {advanced && candidates && candidates.length > 0 && (
        <>
          <Tooltip title="Previous candidate" aria-label="prev">
            <IconButton
              color="primary"
              aria-label="prev"
              onClick={previousCandidate}
            >
              <PreviousIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              "Candidate " +
              (candidates && candidates.length > 0 ? candidateIndex + 1 : 0) +
              " of " +
              (candidates ? candidates.length : 0)
            }
          >
            <Button color="primary">
              {(candidates && candidates.length > 0 ? candidateIndex + 1 : 0) +
                "/" +
                (candidates ? candidates.length : 0)}
            </Button>
          </Tooltip>
          <Tooltip title="Next candidate" aria-label="next">
            <IconButton
              color="primary"
              aria-label="next"
              onClick={nextCandidate}
            >
              <NextIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete candidate" aria-label="lock">
            <IconButton
              color="primary"
              aria-label="lock"
              onClick={deleteCandidate}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      )}
    </>
  );

  return <Container>{editing ? editingButtons() : viewButtons()}</Container>;
}

const Container = styled.div`
  position: absolute;
  right: 0px;
  bottom: 0px;
  color: white;
  background-color: black;
  opacity: 1;
  z-index: 1;
`;
